/**
 * Shared sales tax row for mailed-card and animated e-card checkout summaries (PBI #1515).
 * Omits the row when tax is zero and not loading; shows a loading placeholder while quoting.
 */

import { Group, Loader, Text } from '@mantine/core';
import { semanticColors } from '../../config/theme';
import { OrderTaxLineLabels } from '../../constants/orderTaxLineLabels';
import { formatUsd } from '../../utils/formatUsd';

export interface PriceSummaryTaxLineProps {
  taxUsd?: number | null;
  /** When true, label reads "Estimated sales tax" (scheduled mailed cards). */
  isEstimated?: boolean;
  loading?: boolean;
}

export function PriceSummaryTaxLine({
  taxUsd = null,
  isEstimated = false,
  loading = false,
}: PriceSummaryTaxLineProps) {
  if (loading) {
    const label = isEstimated
      ? OrderTaxLineLabels.estimatedSalesTax
      : OrderTaxLineLabels.salesTax;
    return (
      <Group justify="space-between" wrap="nowrap" data-testid="price-summary-tax-line">
        <Text size="sm" c={semanticColors.text.secondary}>
          {label}
        </Text>
        <Loader size="xs" aria-label="Calculating sales tax" />
      </Group>
    );
  }

  if (taxUsd == null || taxUsd <= 0) {
    return null;
  }

  const label = isEstimated
    ? OrderTaxLineLabels.estimatedSalesTax
    : OrderTaxLineLabels.salesTax;

  return (
    <Group justify="space-between" wrap="nowrap" data-testid="price-summary-tax-line">
      <Text size="sm" c={semanticColors.text.secondary}>
        {label}
      </Text>
      <Text size="sm" fw={500} aria-label={`${label} ${formatUsd(taxUsd)}`}>
        {formatUsd(taxUsd)}
      </Text>
    </Group>
  );
}
